/* The print queue that lives above the pages (desktop PrintQueueContext + PrintJobQueueContext):
 * the cards (with their nest boxes) and the serial print-job queue survive navigation, and the job
 * toast follows the user to every page. A browser reload clears them, as an app restart does on the
 * desktop. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { cancelCommand, copyToJobs, getCommand, listDevices, sendCommand, uploadFile, watchDevice } from '../../api/bridge.js';
import { markPrintFailed, markPrinted } from '../../api/school.js';
import { jobView } from './printRules.js';
import i18n from '../../i18n/index.js';

const Ctx = createContext(null);
export const usePrintStore = () => useContext(Ctx);

const uid = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const ACTIVE = new Set(['preparing', 'sending', 'running']);
const TERMINAL = new Set(['done', 'failed', 'canceled', 'skipped', 'detached']);
const GAP_MS = 1200; // between jobs (desktop PJQ)
const LINGER_MS = 6000; // the toast stays this long after the last job

/** WebP is not printable by the pack (jpg/png/tif): convert it to PNG in the browser. */
async function toPrintable(file) {
  if (file.type !== 'image/webp') return file;
  const bmp = await createImageBitmap(file);
  const c = document.createElement('canvas');
  c.width = bmp.width; c.height = bmp.height;
  c.getContext('2d').drawImage(bmp, 0, 0);
  const blob = await new Promise((res) => c.toBlob(res, 'image/png'));
  return new File([blob], file.name.replace(/\.webp$/i, '.png'), { type: 'image/png' });
}
function readSize(url) {
  return new Promise((res) => { const i = new Image(); i.onload = () => res({ w: i.naturalWidth, h: i.naturalHeight }); i.onerror = () => res({ w: 0, h: 0 }); i.src = url; });
}
/** Every nest-only field on a card (cleared when nesting ends). */
export const clearNest = (it) => ({ ...it, nestPlaced: undefined, nestX: undefined, nestY: undefined, nestW: undefined, nestH: undefined, nestZ: undefined, nestRotated: undefined });

export function PrintProvider({ children }) {
  /* ---------------------------------------------------------------- cards */
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [nestMode, setNestMode] = useState(false);
  const [nestSelId, setNestSelId] = useState(null);
  const [importing, setImporting] = useState(null);
  const [notices, setNotices] = useState([]);
  const notify = useCallback((text, tone = 'yellow', ms = 4500) => {
    const id = uid('n');
    setNotices((n) => [...n.slice(-3), { id, text, tone }]);
    setTimeout(() => setNotices((n) => n.filter((x) => x.id !== id)), ms);
  }, []);
  const dropNotice = (id) => setNotices((n) => n.filter((x) => x.id !== id));

  const patchItem = useCallback((id, p) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...(typeof p === 'function' ? p(x) : p) } : x))), []);

  const addLocal = async (raw) => {
    if (!/^image\/(png|jpeg|webp)$/.test(raw.type)) return;
    const id = uid('f');
    const url = URL.createObjectURL(raw);
    const { w, h } = await readSize(url);
    // A scan already in the print-jobs bucket (Scan page) needs no upload.
    const preKey = raw.__bvKey || null;
    setItems((xs) => [...xs, { id, name: raw.name, raw, url, w, h, orientation: 'portrait', uploading: !preKey, key: preKey, error: null }]);
    setActiveId((a) => a || id);
    if (preKey) return;
    toPrintable(raw).then((pf) => uploadFile(pf)).then((r) => patchItem(id, r.ok ? { uploading: false, key: r.key } : { uploading: false, error: r.error }))
      .catch((e) => patchItem(id, { uploading: false, error: String(e.message || e) }));
  };
  /** A library design / printable / approved request: shown from its small preview, the original copied on the server. */
  const addRemote = async (r) => {
    const id = uid('f');
    const { w, h } = await readSize(r.previewUrl);
    setItems((xs) => [...xs, { id, name: r.name, raw: null, sourceUrl: r.sourceUrl, schoolJobId: r.schoolJobId || null, posterId: r.posterId || null, url: r.previewUrl, w, h, orientation: 'portrait', uploading: true, key: null, error: null }]);
    setActiveId((a) => a || id);
    // The original prints; the ink estimate reads the small preview (same picture, a fraction of the bytes).
    const [c, pv] = await Promise.all([copyToJobs(r.sourceUrl), r.previewUrl && r.previewUrl !== r.sourceUrl ? copyToJobs(r.previewUrl) : Promise.resolve(null)]);
    patchItem(id, c.ok ? { uploading: false, key: c.key, estimateKey: pv && pv.ok ? pv.key : null } : { uploading: false, error: c.error });
  };
  const addFiles = useCallback(async (list) => {
    for (const raw of list) {
      if (raw && raw.remote) { await addRemote(raw); continue; }
      if (raw.type === 'application/pdf' || /\.pdf$/i.test(raw.name)) {
        try {
          setImporting({ name: raw.name, n: 0, total: 0 });
          const { pdfToImages } = await import('./pdfImport.js');
          const { files, failed } = await pdfToImages(raw, (n, total) => setImporting({ name: raw.name, n, total }));
          for (const f of files) await addLocal(f);
          if (failed.length) notify(i18n.t('bv:print.pdf.pagesFailed', { count: failed.length, pages: failed.join(', '), name: raw.name }));
        } catch (e) {
          notify(i18n.t('bv:print.pdf.readFailed', { name: raw.name, error: e.message || e }), 'coral');
        } finally {
          setImporting(null);
        }
        continue;
      }
      if (!/^image\//.test(raw.type)) { notify(i18n.t('bv:print.pdf.notPicture', { name: raw.name })); continue; }
      await addLocal(raw);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeItem = useCallback((id) => {
    setItems((xs) => {
      const rest = xs.filter((x) => x.id !== id);
      setActiveId((a) => (a === id ? (rest[0] ? rest[0].id : null) : a));
      return rest;
    });
    setNestSelId((s) => (s === id ? null : s));
  }, []);

  /* ---------------------------------------------------------------- jobs */
  const [jobs, setJobs] = useState([]);
  const [rating, setRating] = useState(null); // post-print rating card (desktop PrintRatingToast)
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const patchJob = useCallback((id, p) => setJobs((js) => js.map((j) => (j.id === id ? { ...j, ...(typeof p === 'function' ? p(j) : p) } : j))), []);
  const restored = useRef(new Set());
  const lastEnd = useRef(0);

  /** Cards come back when a job is cancelled or skipped (once per batch). */
  const restoreBatch = (job) => {
    if (!job.restore || !job.restore.length || restored.current.has(job.batch)) return;
    const others = jobsRef.current.filter((j) => j.batch === job.batch && j.id !== job.id);
    if (others.some((j) => j.status === 'done' || ACTIVE.has(j.status))) return; // a copy printed: the poster is not "back"
    restored.current.add(job.batch);
    setItems((xs) => [...xs, ...job.restore.filter((r) => !xs.some((x) => x.id === r.id))]);
    setActiveId((a) => a || job.restore[0].id);
  };

  /**
   * Queue `copies` jobs of one print (each copy is its own job, desktop enqueueCopies).
   * spec: {kind, name, thumb, deviceId, deviceName, printer, payload, key?, prepare?, restore, school?, report?, ink?}
   * prepare(signal) → {key, payload?} runs once per batch (copies reuse it).
   */
  const enqueue = useCallback((spec, copies = 1) => {
    const batch = uid('b');
    const shared = { promise: null };
    const total = Math.max(1, Math.min(50, copies | 0));
    const list = Array.from({ length: total }, (_, i) => ({
      ...spec, id: uid('j'), batch, n: i + 1, total, shared, status: 'queued', cid: null, events: [], cancelling: false, error: null, at: Date.now(),
    }));
    setJobs((js) => [...js.filter((j) => !(TERMINAL.has(j.status) && j.dismissed)), ...list]);
    setToastOpen(true);
  }, []);

  // Device online state for the jobs' computers (the command waits on the Bridge while offline).
  const [online, setOnline] = useState({});
  const anyOpen = jobs.some((j) => !TERMINAL.has(j.status));
  useEffect(() => {
    if (!anyOpen) return undefined;
    let stop = false;
    const pull = async () => {
      const r = await listDevices();
      if (!stop && r.ok) setOnline(Object.fromEntries((r.data.devices || []).map((d) => [d.id, !!d.online])));
    };
    pull();
    const t = setInterval(pull, 15000);
    return () => { stop = true; clearInterval(t); };
  }, [anyOpen]);

  // The runner: one job at a time; a failure pauses the queue until Retry or Skip.
  useEffect(() => {
    if (jobs.some((j) => ACTIVE.has(j.status) || (j.status === 'failed' && !j.resolved))) return undefined;
    const next = jobs.find((j) => j.status === 'queued');
    if (!next) return undefined;
    const wait = Math.max(0, lastEnd.current + GAP_MS - Date.now());
    const t = setTimeout(() => start(next.id), wait);
    return () => clearTimeout(t);
  }, [jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  const aborts = useRef(new Map());
  const start = async (id) => {
    const job = jobsRef.current.find((j) => j.id === id);
    if (!job || job.status !== 'queued') return;
    const ac = new AbortController();
    aborts.current.set(id, ac);
    let key = job.key;
    let payload = job.payload;
    if (job.prepare) {
      patchJob(id, { status: 'preparing' });
      try {
        if (!job.shared.promise) job.shared.promise = job.prepare(ac.signal);
        const p = await job.shared.promise;
        key = p.key; payload = { ...payload, ...(p.payload || {}) };
      } catch (e) {
        job.shared.promise = null;
        aborts.current.delete(id);
        if (ac.signal.aborted) { finish(id, 'canceled'); return; }
        finish(id, 'failed', String(e.message || e));
        return;
      }
    }
    if (ac.signal.aborted) { aborts.current.delete(id); finish(id, 'canceled'); return; }
    patchJob(id, { status: 'sending' });
    // bvReport: the Bridge writes the print_jobs row from it once the print finishes (RedCircle /prints + ink usage).
    // School-queue prints are recorded by markPrinted instead, so they carry none.
    const sent = job.school && job.school.jobId ? payload
      : { ...payload, bvReport: { ...(job.report || {}), kind: job.kind || null, name: job.name || null, ink: job.ink || null } };
    const r = await sendCommand(job.deviceId, 'print', sent, key);
    aborts.current.delete(id);
    if (!r.ok) { finish(id, 'failed', r.error); return; }
    patchJob(id, { status: 'running', cid: r.data.commandId, key, payload, events: [], devOnline: r.data.online });
  };

  const finish = (id, status, error) => {
    lastEnd.current = Date.now();
    const j0 = jobsRef.current.find((j) => j.id === id);
    if (!j0) return;
    const job = { ...j0, status, error: error || j0.error, endedAt: Date.now() };
    jobsRef.current = jobsRef.current.map((j) => (j.id === id ? job : j));
    setJobs((js) => js.map((j) => (j.id === id ? { ...j, status, error: error || j.error, endedAt: job.endedAt } : j)));
    setTimeout(() => {
      if (status === 'canceled') restoreBatch(job);
      if (job.school && job.school.jobId && !job.school.reported && (status === 'done' || status === 'failed')) {
        job.school.reported = true;
        if (status === 'failed') markPrintFailed(job.school.jobId, { reason: error || i18n.t('bv:print.job.failed') });
        else markPrinted(job.school.jobId, job.report || {});
      }
      if (status === 'done' && job.ratingPosterId) setRating({ posterId: job.ratingPosterId, at: Date.now() });
      // Desktop post-print pass: re-read the printer so the ink used, the paper left and the status after the
      // print reach the backend (the Helper uploads info snapshot + status; fire-and-forget).
      if ((status === 'done' || status === 'failed') && job.deviceId && job.printer && job.printer.ip) {
        const { ip, model } = job.printer;
        if (status === 'done') sendCommand(job.deviceId, 'printer-info', { ip, model, forceRefresh: true }).catch(() => {});
        sendCommand(job.deviceId, 'printer-live-status', { ip, model, source: 'post_print' }).catch(() => {});
      }
      if (job.onEnd) job.onEnd(status);
    }, 0);
  };

  // The running job: live events through the watch socket, polled every second as a safety net.
  const running = jobs.find((j) => j.status === 'running');
  const runKey = running ? running.id : null;
  useEffect(() => {
    if (!running) return undefined;
    const { id, deviceId, cid } = running;
    let stop = false;
    const pull = async () => {
      const c = await getCommand(deviceId, cid);
      if (stop || !c.ok || !c.data.command) return;
      patchJob(id, { cmdStatus: c.data.command.status, events: c.data.command.events || [] });
    };
    const close = watchDevice(deviceId, (m) => { if (m.id === cid || m.t === 'device') pull(); });
    const t = setInterval(pull, 1000);
    pull();
    return () => { stop = true; close(); clearInterval(t); };
  }, [runKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Running job → its view; a terminal view ends it.
  useEffect(() => {
    if (!running) return;
    const v = viewOf(running, online);
    if (!v.terminal) return;
    if (v.error) finish(running.id, 'failed', v.subtitle);
    else if (v.title === i18n.t('bv:print.job.cancelled')) finish(running.id, 'canceled');
    else finish(running.id, 'done');
  }, [running && running.events, running && running.cmdStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelActive = async () => {
    const j = jobsRef.current.find((x) => ACTIVE.has(x.status));
    if (!j) return;
    if (j.status !== 'running') { const ac = aborts.current.get(j.id); if (ac) ac.abort(); patchJob(j.id, { cancelling: true }); return; }
    patchJob(j.id, { cancelling: true });
    await cancelCommand(j.deviceId, j.cid);
  };
  /** "Remove from queue": stop waiting for a held job; it stays on the printer. */
  const detachActive = () => { const j = jobsRef.current.find((x) => x.status === 'running'); if (j) finish(j.id, 'detached'); };
  const acknowledgeActive = async () => {
    const j = jobsRef.current.find((x) => x.status === 'running');
    if (j) await sendCommand(j.deviceId, 'acknowledge', { jobId: j.cid, ip: j.printer.ip });
  };
  const retryFailed = () => {
    const j = jobsRef.current.find((x) => x.status === 'failed' && !x.resolved);
    if (j) patchJob(j.id, { status: 'queued', error: null, cid: null, events: [], cancelling: false });
  };
  const skipFailed = () => {
    const j = jobsRef.current.find((x) => x.status === 'failed' && !x.resolved);
    if (!j) return;
    patchJob(j.id, { resolved: true, status: 'skipped' });
    restoreBatch(j);
  };
  const removeQueued = (id) => {
    const j = jobsRef.current.find((x) => x.id === id);
    if (!j || j.status !== 'queued') return;
    patchJob(id, { status: 'canceled', dismissed: true });
    restoreBatch(j);
  };
  const clearQueued = () => jobsRef.current.filter((j) => j.status === 'queued').forEach((j) => removeQueued(j.id));

  /* ---------------------------------------------------------------- toast */
  const [toastOpen, setToastOpen] = useState(false);
  const dismissToast = () => { setToastOpen(false); setJobs((js) => js.filter((j) => !TERMINAL.has(j.status) || (j.status === 'failed' && !j.resolved))); };
  const allEnded = jobs.length > 0 && jobs.every((j) => TERMINAL.has(j.status) && !(j.status === 'failed' && !j.resolved));
  useEffect(() => {
    if (!allEnded) return undefined;
    const t = setTimeout(() => { setToastOpen(false); setJobs((js) => (js.every((j) => TERMINAL.has(j.status)) ? [] : js)); }, LINGER_MS);
    return () => clearTimeout(t);
  }, [allEnded, jobs.length]);

  /** Ink of every job still to print (click-time estimates). */
  const pendingInk = useMemo(() => {
    const open = jobs.filter((j) => j.status === 'queued' || ACTIVE.has(j.status));
    return { count: open.length, ml: open.reduce((s, j) => s + ((j.ink && j.ink.ml) || 0), 0), cost: open.reduce((s, j) => s + ((j.ink && j.ink.cost) || 0), 0) };
  }, [jobs]);

  const value = {
    items, setItems, patchItem, addFiles, removeItem, importing, activeId, setActiveId,
    nestMode, setNestMode, nestSelId, setNestSelId,
    notices, notify, dropNotice, rating, clearRating: () => setRating(null),
    jobs, enqueue, cancelActive, detachActive, acknowledgeActive, retryFailed, skipFailed, removeQueued, clearQueued,
    toastOpen, dismissToast, online, pendingInk,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The toast view of one job (desktop derivePrintView over the queue states). */
export function viewOf(j, online = {}) {
  const devOnline = online[j.deviceId] !== undefined ? online[j.deviceId] : j.devOnline !== false;
  switch (j.status) {
    case 'queued': return { title: i18n.t('bv:print.job.waitingToPrint'), subtitle: i18n.t('bv:print.job.waitingToPrintSub'), pct: null, tone: 'blue', canCancel: false, queued: true };
    case 'preparing': return j.cancelling ? { title: i18n.t('bv:print.job.cancelling'), subtitle: i18n.t('bv:common.pleaseWait'), pct: null, tone: 'yellow' }
      : { title: j.kind === 'nest' ? i18n.t('bv:print.job.composingNest') : i18n.t('bv:print.job.preparing'), subtitle: j.kind === 'nest' ? i18n.t('bv:common.pleaseWait') : i18n.t('bv:print.job.preparingImage'), pct: null, tone: 'blue', canCancel: true };
    case 'sending': return { title: i18n.t('bv:print.job.sendingComputer'), subtitle: i18n.t('bv:common.pleaseWait'), pct: null, tone: 'blue', canCancel: false };
    case 'running': return jobView({ events: j.events, status: j.cmdStatus || 'queued', deviceOnline: devOnline, deviceName: j.deviceName, cancelling: j.cancelling });
    case 'done': {
      const v = jobView({ events: j.events, status: j.cmdStatus, deviceOnline: true });
      return v.terminal ? v : { title: i18n.t('bv:print.job.finished'), subtitle: i18n.t('bv:print.job.finishedSub'), pct: 100, tone: 'green', terminal: true };
    }
    case 'failed': return { title: i18n.t('bv:print.job.failed'), subtitle: j.error || i18n.t('bv:print.err.notTaken'), pct: null, tone: 'coral', terminal: true, error: true };
    case 'canceled': return { title: i18n.t('bv:print.job.canceled'), subtitle: i18n.t('bv:print.job.backInList'), pct: null, tone: 'yellow', terminal: true };
    case 'skipped': return { title: i18n.t('bv:print.job.skipped'), subtitle: i18n.t('bv:print.job.backInList'), pct: null, tone: 'yellow', terminal: true };
    case 'detached': return { title: i18n.t('bv:print.job.detached'), subtitle: i18n.t('bv:print.job.detachedSub'), pct: null, tone: 'yellow', terminal: true };
    default: return { title: '', subtitle: '', pct: null, tone: 'blue' };
  }
}
